import { Link, Navigate } from "react-router-dom";
import { ChevronRight, Bell } from "lucide-react";
import { SCREENS } from "@/constants/testIds";
import { getCurrentSocio } from "@/data/socioAuthStore";
import SaldoCard from "@/screens/app/_SaldoCard";

/**
 * Mi Perfil · consume el snapshot autenticado del socio actual.
 * Si no hay sesión, redirige al login.
 */
export default function MiPerfil() {
  const socio = getCurrentSocio();
  if (!socio) return <Navigate to="/login" replace />;

  const nombre = socio.nombre || "Socio";
  const numeroSocio = socio.numeroSocio || "—";
  const iniciales = nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "S";

  // Privacidad, Términos y Ayuda se ocultan hasta tener pantalla y contenido aprobado por el Casino.
  const secciones = [
    { label: "Notificaciones", icon: Bell, to: "/app/notificaciones" },
  ];

  return (
    <div data-testid={SCREENS.miPerfil} className="flex flex-col gap-10">
      {/* Encabezado */}
      <header className="animate-fade-up">
        <span className="overline text-accent">Mi Perfil</span>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight">
          Bienvenido
        </h1>
      </header>

      {/* Tarjeta de socio */}
      <section className="animate-fade-up delay-100">
        <div className="relative overflow-hidden rounded-2xl border hairline bg-card p-6 md:p-8 shadow-soft">
          <div
            className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-60 dark:opacity-30 blur-3xl"
            style={{ background: "hsl(var(--accent) / 0.35)" }}
          />
          <div className="relative flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground grid place-items-center ring-1 ring-accent/50 shadow-soft">
              <span className="font-heading text-xl">{iniciales}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p
                data-testid="perfil-nombre"
                className="font-heading text-xl text-foreground truncate"
              >
                {nombre}
              </p>
              <p
                data-testid="perfil-numero-socio"
                className="text-xs text-muted-foreground mt-1"
              >
                No. de socio · {numeroSocio}
              </p>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-4 text-xs">
            {socio.membresiaBloqueada ? (
              <span
                data-testid="perfil-estatus"
                className="inline-flex items-center gap-2 rounded-full border border-destructive/40 bg-destructive/10 text-destructive px-2.5 py-0.5 uppercase tracking-wide"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Membresía bloqueada
              </span>
            ) : (
              <span
                data-testid="perfil-estatus"
                className="inline-flex items-center gap-2 text-muted-foreground"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                {socio.tipoSocio || "Socio"}
                {socio.estatus ? ` · ${socio.estatus}` : ""}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Mi saldo · Sprint 40 · sólo total + desglose, sin historiales */}
      <SaldoCard />

      {/* Accesos rápidos */}
      <section className="animate-fade-up delay-200">
        <h2 className="font-heading text-lg text-foreground mb-3">
          Preferencias
        </h2>
        <ul className="flex flex-col rounded-2xl border hairline bg-card overflow-hidden">
          {secciones.map(({ label, icon: Icon, to }, i) => (
            <li key={label}>
              <Link
                to={to}
                data-testid={`perfil-item-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-muted/60 transition-colors"
              >
                <span className="w-9 h-9 grid place-items-center rounded-xl bg-muted text-foreground/80">
                  <Icon className="w-4 h-4" strokeWidth={1.6} />
                </span>
                <span className="flex-1 text-sm text-foreground">{label}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Link>
              {i < secciones.length - 1 && (
                <div className="mx-5 border-b hairline" />
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
